import React, {useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import Alert from '../../Alert';
import OrderList from './OrderList';
import {ORDER_SEGMENTS} from './orderFilters';
import useSellerOrders from './useSellerOrders';
import './Orders.css';

function responseError(body, fallback) {
  return body?.message || body?.error || fallback;
}

function OrdersManager() {
  const {fetch: authFetch, API_URL} = useAuth();
  const {orders, meta, loading, error, reload} = useSellerOrders();
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyOrderId, setBusyOrderId] = useState(null);

  const runAction = async (order, action, successMessage) => {
    setBusyOrderId(order.id);
    setActionMessage('');
    setActionError('');

    try {
      const response = await authFetch(
        `${API_URL}/v1/seller/orders/${order.id}/${action}`,
        {method: 'POST'},
      );
      const body = await response.json();
      if (!response.ok) {
        throw new Error(responseError(body, `Failed to ${action} order`));
      }
      setActionMessage(successMessage);
      await reload();
    } catch (actionFailure) {
      setActionError(actionFailure.message);
    } finally {
      setBusyOrderId(null);
    }
  };

  const markPaid = order => runAction(order, 'markpaid', 'Order marked as paid.');
  const deleteOrder = order => {
    if (!window.confirm('Delete this unpaid order? This cannot be undone.')) return;
    runAction(order, 'delete', 'Order deleted.');
  };

  if (loading) {
    return (
      <div className="orders-state" role="status">
        <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
        Loading orders…
      </div>
    );
  }

  if (error) {
    return (
      <Alert type="error" className="orders-state">
        <p>{error}</p>
        <button type="button" onClick={reload}>Try again</button>
      </Alert>
    );
  }

  return (
    <div className="orders-manager">
      {actionMessage && <Alert type="success">{actionMessage}</Alert>}
      {actionError && <Alert type="error">{actionError}</Alert>}

      <OrderList
        id="needing-attention"
        title="Orders Needing Attention"
        orders={orders}
        segment={ORDER_SEGMENTS.ATTENTION}
        hideWhenEmpty
      >
        <p>
          <a href="/account/orders/labels" className="help-link">
            Print Packing, Address, and Return Labels for these orders{' '}
            <i className="fa-solid fa-print" aria-hidden="true"></i>
          </a>
        </p>
        <p>
          <a href="/help/processing-orders" className="help-link" target="_blank">
            Learn how to process orders{' '}
            <i className="fa-solid fa-question-circle" aria-hidden="true"></i>
          </a>
        </p>
      </OrderList>

      <OrderList
        id="unpaid"
        title="Unpaid Orders"
        totalsLabel="Unpaid order totals"
        totals={meta?.totals?.unpaid}
        orders={orders}
        segment={ORDER_SEGMENTS.UNPAID}
        emptyMessage="You do not have any unpaid orders."
        busyOrderId={busyOrderId}
        onMarkPaid={markPaid}
        onDelete={deleteOrder}
        beforeTable={(
          <div className="info">
            <p>
              <strong>Important:</strong> Only mark an order as paid after you have confirmed
              receiving payment through your off-site payment method (PayPal, Venmo, CashApp,
              etc.).
            </p>
          </div>
        )}
      >
        <p>
          <a href="/help/unpaid-orders" className="help-link" target="_blank">
            What to do with unpaid orders{' '}
            <i className="fa-solid fa-question-circle" aria-hidden="true"></i>
          </a>
        </p>
      </OrderList>

      <OrderList
        id="paid"
        title="Paid Orders"
        totalsLabel="Paid order totals"
        totals={meta?.totals?.paid}
        orders={orders}
        segment={ORDER_SEGMENTS.PAID}
        emptyMessage="You do not have any paid orders yet."
      />
    </div>
  );
}

export default OrdersManager;
